import "./style.css";

import React, { useCallback, useState } from "react";
import ReactSlider from "react-slider";
import { useObservable } from "react-use-observable";

import {
  Actions,
  controlsService,
} from "../../../../../../../services/controls";
import { volumeService } from "../../../../../../../services/volume";
import VolumeDownIcon from "../../../../../../icons/VolumeDown";
import VolumeOffIcon from "../../../../../../icons/VolumeOff";
import VolumeUpIcon from "../../../../../../icons/VolumeUp";
import Button from "../Button";
import { useCustomPlayerContext } from "../../../../../../../contexts/CustomPlayerContext";
import { darkenColor } from "../../../../../../../helpers/darkenColor";
import { useIsMobile } from "../../../../../../../hooks/useIsMobile";

const VolumeControl: React.FC = () => {
  const isMobile = useIsMobile();
  const [volume] = useObservable(() => volumeService.listenVolume(), []);
  const [isMuted] = useObservable(() => volumeService.listenIsMuted(), []);
  const [before, setBefore] = useState(100);
  const [classHover, setClassHover] = useState(false);
  const [thumbClick, setThumbClick] = useState(false);
  const { customPlayerPreferences } = useCustomPlayerContext();

  const registerAction = useCallback(() => {
    controlsService.registerAction({
      name: Actions.CLICK_MUTED,
      data: !isMuted,
    });
  }, [isMuted]);

  const handleChange = useCallback(
    (value: number) => {
      if (isMuted)
        controlsService.registerAction({
          name: Actions.CLICK_MUTED,
          data: false,
        });
      controlsService.registerAction({
        name: Actions.CLICK_VOLUME_CHANGE,
        data: value,
      });
    },
    [isMuted]
  );

  const handleBeforeChange = (value: number) => {
    if (value !== 0) setBefore(value);
    setThumbClick(true);
  };

  const handleAfterChange = (value: number) => {
    if (value === 0) {
      controlsService.registerAction({
        name: Actions.CLICK_VOLUME_CHANGE,
        data: before,
      });
      controlsService.registerAction({ name: Actions.CLICK_MUTED, data: true });
    }
    setThumbClick(false);
  };

  const handleIconChange = () => {
    if (isMuted || volume! === 0) {
      return <VolumeOffIcon customStyles={{ color: customPlayerPreferences.customTextColor ? customPlayerPreferences.customTextColor : undefined }} />;
    }

    if (volume! > 50) {
      return <VolumeUpIcon customStyles={{ color: customPlayerPreferences.customTextColor ? customPlayerPreferences.customTextColor : undefined }} />;
    }

    if (volume! > 0) {
      return <VolumeDownIcon customStyles={{ color: customPlayerPreferences.customTextColor ? customPlayerPreferences.customTextColor : undefined }} />;
    }

    return <VolumeUpIcon customStyles={{ color: customPlayerPreferences.customTextColor ? customPlayerPreferences.customTextColor : undefined }} />;
  };

  return (
    <div
      onMouseOver={() => setClassHover(true)}
      onMouseOut={() => setClassHover(false)}
      style={{ paddingRight: customPlayerPreferences.allowVolume && (classHover || thumbClick) ? "0.5rem" : "" }}
    >
      <Button onClick={() => registerAction()}>{handleIconChange()}</Button>

      {!isMobile && customPlayerPreferences.allowVolume && (
        <ReactSlider
          className={`volume ${classHover || thumbClick ? "volume--show" : ""}`}
          thumbClassName="volume__thumb"
          trackClassName="volume__track"
          defaultValue={100}
          value={isMuted ? 0 : volume!}
          onAfterChange={(value) => handleAfterChange(value)}
          onBeforeChange={(value) => handleBeforeChange(value)}
          onChange={(value) => handleChange(value)}
          renderThumb={(props) => (
            <div
              {...props}
              style={{
                ...props.style,
                background: customPlayerPreferences.customTextColor ? customPlayerPreferences.customTextColor : undefined,
                border: customPlayerPreferences.customTextColor ? `1px solid ${customPlayerPreferences.customTextColor}` : undefined,
              }}
              onMouseDown={() => setThumbClick(true)}
              onMouseUp={() => setThumbClick(false)}
            ></div>
          )}
          renderTrack={(props, state) => (
            <div
              {...props}
              style={{
                ...props.style,
                background: state.index === 0 ? customPlayerPreferences.customTextColor : darkenColor({
                  hexColor: customPlayerPreferences.customTextColor,
                  percentage: 0.6,
                }),
              }}
            />
          )}
        />
      )}
    </div>
  );
};

export default VolumeControl;
